import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import { supabaseClient } from '../../utils/supabase';
import { useGlobalMetadata } from '../../utils/providers';
import { useMyCapabilities } from '../../utils/metadata/capabilities';
import { useRecordNavigator } from '../../hooks/useRecordNavigator';
import { useFillGridHeight } from '../../hooks/useFillGridHeight';
import { serializeParams, withOverlayTarget } from '../../utils/recordTargets';
import WorkspaceGrid from '../WorkspaceGrid/WorkspaceGrid';
import { MaterializedReportStatus } from '../ui/MaterializedReportStatus';
import { fmtDay } from '../LeadImport/ui';
import type { LeadRow } from './LeadSourcePage';
import SourceVendorCard, { type SourceVendor } from './SourceVendorCard';

interface ImportRow {
  id: number;
  file_name: string | null;
  sub_source: string | null;
  batch_date: string | null;
  leads: number;
  new_identities: number;
  repeats: number;
  known_merchant: number;
  assigned: number;
  promoted: number;
  submitted_merchants: number;
  funded_deals: number;
  funded_amount: number;
  cost: number | null;
  cost_per_lead: number | null;
}

interface MonthRow { month: string; imports: number; leads: number; new_identities: number; cost: number | null; funded_deals: number }
interface SubSourceRow { sub_source: string | null; leads: number; new_identities: number }
interface OverlapRow { id: number | null; source: string | null; phones: number }
interface RepRow { id: number; name: string; leads: number; merchants: number; funded_deals: number }

/** `lead_import_report_source_detail`: one source's committed imports and what became of them. */
export interface SourceImportReport {
  vendor: SourceVendor | null;
  imports: number;
  first_import: string | null;
  last_import: string | null;
  totals: {
    leads: number; phones: number; new_identities: number; repeats: number; known_merchant: number;
    deal_history: number; assigned: number; with_rep: number; promoted: number; merchants: number;
  };
  cost: number | null;
  costed_imports: number;
  cost_per_lead: number | null;
  cost_per_new_identity: number | null;
  outcomes: {
    submitted_merchants: number; funded_merchants: number; funded_deals: number; funded_amount: number;
    funded_deals_new_merchants: number; cost_per_funded_deal: number | null;
  };
  months: MonthRow[];
  batches: ImportRow[];
  sub_sources: SubSourceRow[];
  overlap: OverlapRow[];
  reps: RepRow[];
}

async function loadSourceImportReport(source: number): Promise<SourceImportReport> {
  const { data, error } = await (supabaseClient as any).rpc('lead_import_report_source_detail', { p_source: source });
  if (error) throw error;
  return data as SourceImportReport;
}

const int = (n: number | null | undefined) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const money = (n: number | null | undefined) => (n == null ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n));
const exactMoney = (n: number | null | undefined) => (n == null ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n));
function shortMoney(n: number | null | undefined): string {
  if (n == null) return '—';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(Math.abs(n) >= 10_000_000 ? 0 : 1)}M`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return money(n);
}
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
const monthLabel = (month: string) => new Date(`${month}-01T12:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

function Stat({ label, value, sub, accent, large }: { label: string; value: React.ReactNode; sub: React.ReactNode; accent?: string; large?: boolean }) {
  return <div className={`rep-stat ${large ? 'large' : ''}`}><span className="rep-stat-label">{label}</span><b style={{ color: accent }}>{value}</b><small>{sub}</small></div>;
}

function Rate({ label, value, sub, accent }: { label: string; value: number; sub: string; accent: string }) {
  return <div className="rep-rate"><div className="rep-rate-ring" style={{ background: `conic-gradient(${accent} ${value * 3.6}deg,var(--border-soft) 0)` }}><span>{value}%</span></div><div><span className="rep-stat-label">{label}</span><small>{sub}</small></div></div>;
}

const num = (headerName: string, field: string, format: (v: number | null) => string = int, extra: Partial<ColDef> = {}): ColDef<any> => ({
  headerName, field, type: 'rightAligned', flex: 90, minWidth: Math.max(110, headerName.length * 9 + 70), cellClass: 'numeric', valueFormatter: (p) => format(p.value), ...extra,
});

/** A part-of-whole percentage column, worked out per row. */
function shareColumn<T>(headerName: string, colId: string, share: (row: T) => number): ColDef<T> {
  return { headerName, colId, type: 'rightAligned', flex: 90, minWidth: Math.max(110, headerName.length * 9 + 70), cellClass: 'numeric',
    valueGetter: (p) => (p.data ? share(p.data) : 0), valueFormatter: (p) => `${p.value}%` };
}

const IMPORT_COLUMNS: ColDef<ImportRow>[] = [
  { headerName: 'IMPORT', field: 'file_name', flex: 220, minWidth: 200, cellClass: 'primary-cell', valueFormatter: (p) => p.value || `Import ${p.data?.id}` },
  { headerName: 'DATE', field: 'batch_date', flex: 100, minWidth: 100, valueFormatter: (p) => fmtDay(p.value) },
  { headerName: 'SUB-SOURCE', field: 'sub_source', flex: 110, minWidth: 100, valueFormatter: (p) => p.value || '—' },
  num('LEADS', 'leads'),
  num('NEW', 'new_identities', int, { headerTooltip: 'Never seen before this import' }),
  num('REPEATS', 'repeats'),
  num('KNOWN', 'known_merchant', int, { headerTooltip: 'Already a merchant when the lead came in' }),
  num('TO REPS', 'assigned', int, { headerTooltip: 'Given to a rep at some point' }),
  num('MADE', 'promoted', int, { headerTooltip: 'Merchants made from these leads' }),
  num('FUNDED', 'funded_deals', int, { cellClass: 'numeric funded-number' }),
  num('FUNDED $', 'funded_amount', (v) => (v ? money(v) : '—'), { cellClass: 'numeric funded-number' }),
  num('COST', 'cost', money),
  num('PER LEAD', 'cost_per_lead', exactMoney),
];

const MONTH_COLUMNS: ColDef<MonthRow>[] = [
  { headerName: 'MONTH', field: 'month', flex: 140, minWidth: 120, cellClass: 'primary-cell', valueFormatter: (p) => monthLabel(p.value) },
  num('IMPORTS', 'imports'),
  num('LEADS', 'leads'),
  num('NEW TO US', 'new_identities'),
  shareColumn<MonthRow>('NEW SHARE', 'new_share', (r) => pct(r.new_identities, r.leads)),
  num('COST', 'cost', money),
  num('FUNDED', 'funded_deals', int, { cellClass: 'numeric funded-number' }),
];

const SUB_SOURCE_COLUMNS: ColDef<SubSourceRow>[] = [
  { headerName: 'SUB-SOURCE', field: 'sub_source', flex: 200, minWidth: 160, cellClass: 'primary-cell', valueFormatter: (p) => p.value || 'None given' },
  num('LEADS', 'leads'),
  num('NEW TO US', 'new_identities'),
  shareColumn<SubSourceRow>('NEW SHARE', 'new_share', (r) => pct(r.new_identities, r.leads)),
];

const REP_COLUMNS: ColDef<RepRow>[] = [
  { headerName: 'REP', field: 'name', flex: 200, minWidth: 160, cellClass: 'primary-cell' },
  num('LEADS HELD', 'leads'),
  num('MERCHANTS', 'merchants'),
  num('FUNDED', 'funded_deals', int, { cellClass: 'numeric funded-number' }),
];

type Tab = 'imports' | 'months' | 'sub_sources' | 'overlap' | 'reps';

function ImportReport({ source, report }: { source: LeadRow; report: SourceImportReport }) {
  const [tab, setTab] = useState<Tab>('imports');
  const fill = useFillGridHeight(380);
  const navigate = useNavigate();
  const { openRecord } = useRecordNavigator();
  const { data: metadata } = useGlobalMetadata();
  const t = report.totals;
  const o = report.outcomes;
  const importsRoute = metadata?.routeData?.tableToRouteName?.lead_import || 'lead_import';

  const overlapColumns: ColDef<OverlapRow>[] = [
    { headerName: 'OTHER SOURCE', field: 'source', flex: 200, minWidth: 160, cellClass: 'primary-cell', valueFormatter: (p) => p.value || 'No source' },
    num('SHARED PHONES', 'phones'),
    shareColumn<OverlapRow>('OF THIS SOURCE', 'share', (r) => pct(r.phones, t.phones)),
  ];

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: 'imports', label: 'Imports', count: report.batches.length },
    { id: 'months', label: 'By month', count: report.months.length },
    { id: 'sub_sources', label: 'Sub-sources', count: report.sub_sources.length },
    { id: 'overlap', label: 'Shared phones', count: report.overlap.length },
    { id: 'reps', label: 'Reps', count: report.reps.length },
  ];

  if (report.imports === 0) {
    return (
      <section className="rep-data-card lead-source-empty">
        <b>No imports from {source.source} yet</b>
        <span>Once a file from this source is imported, its leads, repeats, cost and outcomes show up here.</span>
      </section>
    );
  }

  const grid = (() => {
    const common = { height: fill.height, loading: false };
    switch (tab) {
      case 'imports':
        return <WorkspaceGrid<ImportRow> {...common} rows={report.batches} columns={IMPORT_COLUMNS} getRowId={(r) => String(r.id)}
          onRowClick={(r) => navigate(`/${importsRoute}?batch=${r.id}`)} storageKey="workspace_grid_lead_source_imports" emptyMessage="No imports." />;
      case 'months':
        return <WorkspaceGrid<MonthRow> {...common} rows={[...report.months].reverse()} columns={MONTH_COLUMNS} getRowId={(r) => r.month} emptyMessage="No imports." />;
      case 'sub_sources':
        return <WorkspaceGrid<SubSourceRow> {...common} rows={report.sub_sources} columns={SUB_SOURCE_COLUMNS} getRowId={(r) => r.sub_source ?? ''} emptyMessage="No sub-sources." />;
      case 'overlap':
        return <WorkspaceGrid<OverlapRow> {...common} rows={report.overlap} columns={overlapColumns} getRowId={(r) => String(r.id)}
          onRowClick={(r) => r.id != null && openRecord({ tableName: 'lead_source', id: r.id, label: r.source ?? undefined })}
          emptyMessage="No phone from this source came in from any other source." />;
      case 'reps':
        return <WorkspaceGrid<RepRow> {...common} rows={report.reps} columns={REP_COLUMNS} getRowId={(r) => String(r.id)}
          onRowClick={(r) => openRecord({ tableName: 'rep', id: r.id, label: r.name })} emptyMessage="No rep holds a lead from this source." />;
    }
  })();

  return (
    <>
      <div className="rep-stat-grid primary">
        <Stat large label="Leads imported" value={int(t.leads)} sub={`${int(report.imports)} imports, ${fmtDay(report.first_import)} to ${fmtDay(report.last_import)}`} />
        <Stat large label="New to us" value={int(t.new_identities)} sub={`${pct(t.new_identities, t.leads)}% never seen before`} accent="var(--accent)" />
        <Stat large label="Became merchants" value={int(t.merchants)} sub={`${int(t.promoted)} made from a lead here`} accent="var(--accent)" />
        <Stat large label="Funded after import" value={int(o.funded_deals)} sub={`${shortMoney(o.funded_amount)} from ${int(o.funded_merchants)} merchants`} accent="var(--green)" />
        <Stat large label="Lead cost" value={shortMoney(report.cost)}
          sub={report.cost == null ? 'no purchase recorded' : `${exactMoney(report.cost_per_lead)} per lead, ${int(report.costed_imports)} of ${int(report.imports)} imports costed`} accent={report.cost == null ? undefined : 'var(--red)'} />
      </div>
      <div className="rep-stat-grid secondary">
        <Stat label="Repeats" value={int(t.repeats)} sub="phone seen in an earlier import" accent="var(--amber)" />
        <Stat label="Already merchants" value={int(t.known_merchant)} sub={`${int(t.deal_history)} with a deal history`} accent="var(--amber)" />
        <Stat label="Cost per new lead" value={exactMoney(report.cost_per_new_identity)}
          sub={o.cost_per_funded_deal == null ? 'no funded deal yet' : `${money(o.cost_per_funded_deal)} per funded deal`} />
        <Rate label="Given to reps" value={pct(t.assigned, t.leads)} sub={`${int(t.assigned)} of ${int(t.leads)}, ${int(t.with_rep)} held now`} accent="var(--accent)" />
        <Rate label="Submitted" value={pct(o.submitted_merchants, t.merchants)} sub={`${int(o.submitted_merchants)} of ${int(t.merchants)} merchants`} accent="var(--green)" />
      </div>
      <section className="rep-data-card">
        <div className="rep-tabs" role="tablist">
          {tabs.map((x) => (
            <button key={x.id} role="tab" aria-selected={tab === x.id} className={tab === x.id ? 'active' : ''} onClick={() => setTab(x.id)}>
              {x.label} <em>{x.count}</em>
            </button>
          ))}
          <span className="rep-tabs-note">committed imports, all time</span>
        </div>
        <div ref={fill.ref}>{grid}</div>
      </section>
    </>
  );
}

/** Opens the source's own fields in the record modal, carried in the overlay key so its page stays open underneath. */
export function LeadSourceEditButton({ source }: { source: LeadRow }) {
  const navigate = useNavigate();
  const location = useLocation();
  const editSource = () => {
    const params = withOverlayTarget(new URLSearchParams(location.search), { tableName: 'lead_source', id: String(source.id) });
    navigate(`${location.pathname}?${serializeParams(params)}`);
  };
  return (
    <button className="rep-chip-edit" onClick={editSource} title={`Edit ${source.source}`} aria-label={`Edit ${source.source}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
    </button>
  );
}

/** What a source's imports brought in, below its deal tabs; shown only to lead import users. */
export default function LeadSourceImports({ source }: { source: LeadRow }) {
  const { hasCapability, isLoading: capabilitiesLoading } = useMyCapabilities();
  const canSeeImports = !capabilitiesLoading && hasCapability('lead_import');
  const report = useQuery({
    queryKey: ['lead-source-import-report', source.id],
    queryFn: () => loadSourceImportReport(source.id),
    enabled: canSeeImports,
    staleTime: 2 * 60_000,
  });

  if (capabilitiesLoading || !canSeeImports) return null;
  if (report.isLoading) return <div className="rep-page-state"><div className="rep-spinner" /></div>;
  if (report.error) {
    return <div className="rep-page-state"><strong>Unable to load the imports from this source</strong><span>{(report.error as Error).message}</span></div>;
  }
  if (!report.data) return null;
  return (
    <section aria-label="Imports from this source">
      <MaterializedReportStatus name="lead_import_reports" refetchKeys={[['lead-source-import-report']]} />
      <SourceVendorCard source={source.id} vendor={report.data.vendor} />
      <ImportReport source={source} report={report.data} />
    </section>
  );
}
